import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";

/**
 * Beyond Sure — "Add your phone number" nudge modal.
 *
 * Mounted in ChatRoute behind PHONE_CAPTURE_POPUP_ENABLED. When the flag is
 * on AND the logged-in user has no phone on file AND they haven't dismissed
 * the nudge, the modal floats in once per session (afterward respect the
 * dismissal flag). The user can:
 *   • Add a phone now → 2-step inside the modal: enter number → enter OTP →
 *     POST /api/auth/phone/verify-otp with link_mode=true (the existing
 *     account-linking path at server.py:10118)
 *   • "Maybe later" → record a dismissal in localStorage so we don't nag
 *     again for 30 days.
 *
 * The modal NEVER mounts when the user has no session token (we'd have
 * nothing to link to). It also self-hides if PHONE_CAPTURE_POPUP_ENABLED is
 * false even when mounted — so the ChatRoute mount line is a no-op until
 * the flag flips.
 *
 * Why a modal not a banner: nudging users to add phone is a *conversion*
 * moment, and a banner is easier to mentally filter out. A polite, single,
 * dismissible modal converts ~3× a banner in our internal A/Bs (per the
 * launch-chain notes, L11). It's also easier to instrument — modal-shown
 * and modal-dismissed are clean events.
 */

import { PHONE_CAPTURE_POPUP_ENABLED } from "../lib/featureFlags";

const DISMISS_KEY = "bsure_add_phone_dismissed_at";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

type Step = "intro" | "phone" | "otp" | "done";

function toIndianMobile(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10 && /^[6-9]/.test(digits)) return digits;
  if (digits.length === 11 && digits.startsWith("0") && /^0[6-9]/.test(digits))
    return digits.slice(1);
  if (digits.length === 12 && digits.startsWith("91") && /^91[6-9]/.test(digits))
    return digits.slice(2);
  return "";
}

function isValidIndianMobile(raw: string): boolean {
  return toIndianMobile(raw).length === 10;
}

function shouldShow(): boolean {
  if (!PHONE_CAPTURE_POPUP_ENABLED) return false;
  if (typeof window === "undefined") return false;
  // Must be logged in
  const tok = window.localStorage.getItem("bsure_token") ||
              window.localStorage.getItem("ig_t") || "";
  if (!tok) return false;
  // Must NOT already have a phone on file
  let user: { phone?: string } = {};
  try { user = JSON.parse(window.localStorage.getItem("ig_u") || "{}"); } catch { /* */ }
  if (user.phone && user.phone.length > 0) return false;
  // Must not have dismissed recently
  const dismissedAtRaw = window.localStorage.getItem(DISMISS_KEY);
  if (dismissedAtRaw) {
    const ts = parseInt(dismissedAtRaw, 10);
    if (!Number.isNaN(ts) && Date.now() - ts < DISMISS_TTL_MS) return false;
  }
  return true;
}

export function AddPhoneModal() {
  const [open, setOpen] = useState<boolean>(false);
  const [step, setStep] = useState<Step>("intro");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  // Number we actually sent the OTP to (normalized) — keep separate from the
  // input field so a typo-fix during OTP entry doesn't break the verify call.
  const sentToRef = useRef<string>("");

  // Show decision is computed exactly once on mount. We deliberately don't
  // poll localStorage for changes — the moment for this nudge is the first
  // chat-screen load, not a reactive watcher.
  useEffect(() => {
    if (shouldShow()) {
      // Tiny delay so it doesn't slam in during initial layout shift.
      const id = window.setTimeout(() => setOpen(true), 1200);
      return () => window.clearTimeout(id);
    }
  }, []);

  function dismissForLater() {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* private mode — best-effort */ }
    setOpen(false);
  }

  async function startSendOtp() {
    setErrMsg(null);
    if (!isValidIndianMobile(phone)) {
      setErrMsg("Enter a 10-digit Indian mobile number.");
      return;
    }
    setLoading(true);
    const normalized = toIndianMobile(phone);
    try {
      const r = await fetch("/api/auth/phone/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: normalized }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErrMsg((data && (data.message || data.error)) ||
                  "Could not send code. Please try again.");
        setLoading(false);
        return;
      }
      sentToRef.current = normalized;
      setStep("otp");
      setLoading(false);
    } catch {
      setErrMsg("Network error. Please try again.");
      setLoading(false);
    }
  }

  async function startVerify() {
    setErrMsg(null);
    if (code.length !== 6) {
      setErrMsg("Enter the 6-digit code we sent.");
      return;
    }
    setLoading(true);
    const tok = window.localStorage.getItem("bsure_token") ||
                window.localStorage.getItem("ig_t") || "";
    try {
      const r = await fetch("/api/auth/phone/verify-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // The server's link_mode path requires an authenticated session
          // (server.py:10119-10121). Pass the existing bearer token so
          // /api/auth/phone/verify-otp links the phone TO this account
          // instead of creating a fresh one.
          Authorization: tok ? `Bearer ${tok}` : "",
        },
        body: JSON.stringify({
          phone: sentToRef.current,
          code,
          link: true, // ← server.py L10118 link_mode
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.linked) {
        // 409 = phone already linked to another account; 401 = code wrong;
        // both surface server's message verbatim.
        setErrMsg((data && (data.message || data.error)) ||
                  "That code didn't match. Try again or resend.");
        setLoading(false);
        return;
      }
      // Update the cached user blob so we don't re-nudge on the next mount.
      try {
        const raw = window.localStorage.getItem("ig_u");
        if (raw) {
          const u = JSON.parse(raw);
          u.phone = sentToRef.current;
          window.localStorage.setItem("ig_u", JSON.stringify(u));
        }
      } catch { /* */ }
      setStep("done");
      setLoading(false);
      // Close after a brief "saved" beat
      window.setTimeout(() => setOpen(false), 1200);
    } catch {
      setErrMsg("Network error. Please try again.");
      setLoading(false);
    }
  }

  const overlayClass = useMemo(() =>
    "fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
  , []);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="add-phone-overlay"
          className={overlayClass}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            // Click-outside dismisses for-later (same as the explicit button).
            if (e.target === e.currentTarget) dismissForLater();
          }}
        >
          <motion.div
            role="dialog"
            aria-labelledby="add-phone-title"
            aria-modal="true"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
            className="bsure-ui w-full max-w-[420px] rounded-[16px] bg-[var(--color-paper)] p-6 shadow-xl"
          >
            {step === "intro" ? (
              <div className="flex flex-col gap-4">
                <h2 id="add-phone-title" className="bsure-display text-[1.5rem] leading-[1.1] text-[var(--color-ink)]">
                  Add your mobile number?
                </h2>
                <p className="text-[14px] leading-[1.55] text-[var(--color-ink-60)]">
                  Add an Indian mobile number so we can text policy alerts,
                  renewal nudges, and let you sign in faster. SMS only — we
                  never share it.
                </p>
                <div className="flex gap-2 mt-1">
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={dismissForLater}
                  >
                    Maybe later
                  </Button>
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={() => setStep("phone")}
                  >
                    Add number
                  </Button>
                </div>
              </div>
            ) : step === "phone" ? (
              <div className="flex flex-col gap-4">
                <h2 className="bsure-display text-[1.5rem] leading-[1.1] text-[var(--color-ink)]">
                  Your mobile number
                </h2>
                {errMsg ? (
                  <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-900">
                    {errMsg}
                  </div>
                ) : null}
                <Input
                  label="Mobile number"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  helper="Indian 10-digit mobile starting 6/7/8/9."
                />
                <div className="flex gap-2 mt-1">
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={dismissForLater}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    fullWidth
                    loading={loading}
                    onClick={startSendOtp}
                    disabled={!isValidIndianMobile(phone) || loading}
                  >
                    Send SMS code
                  </Button>
                </div>
              </div>
            ) : step === "otp" ? (
              <div className="flex flex-col gap-4">
                <h2 className="bsure-display text-[1.5rem] leading-[1.1] text-[var(--color-ink)]">
                  Enter the code
                </h2>
                <p className="text-[13.5px] text-[var(--color-ink-60)]">
                  We sent a 6-digit code to{" "}
                  <span className="text-[var(--color-ink)] font-medium">
                    +91 {sentToRef.current}
                  </span>.
                </p>
                {errMsg ? (
                  <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-900">
                    {errMsg}
                  </div>
                ) : null}
                <Input
                  label="6-digit code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
                <div className="flex gap-2 mt-1">
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => setStep("phone")}
                  >
                    Edit number
                  </Button>
                  <Button
                    variant="primary"
                    fullWidth
                    loading={loading}
                    onClick={startVerify}
                    disabled={code.length !== 6 || loading}
                  >
                    Verify
                  </Button>
                </div>
              </div>
            ) : (
              // "done" — quick confirmation; auto-closes after 1.2s
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <div
                  aria-hidden="true"
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: "var(--color-brand)",
                    color: "var(--color-paper)",
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="bsure-display text-[1.25rem] leading-[1.1] text-[var(--color-ink)]">
                  Number saved
                </div>
                <p className="text-[13.5px] text-[var(--color-ink-60)]">
                  You'll start getting SMS nudges before renewals.
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export default AddPhoneModal;
